import React, { useState } from 'react'
import { encodeToBase64, isACorrectFormat } from '../../../core/utils'
import { DEFAULT_FILE_SIZE } from '../constants'

/**
 * @typedef {object} FileUploaderAgnosticProps
 * @property {string} [idName] - Identificador personalizado para el input (si no se usa name).
 * @property {string} name - Nombre del campo para identificarlo en el evento onChange.
 * @property {Array<string>} accept - Array de tipos MIME aceptados.
 * @property {function(FileUploaderAgnosticChangeEvent):void} onSuccess - Callback invocado al seleccionar archivo válido.
 * @property {function(object):void} onError - Callback invocado al encontrar un error.
 * @property {boolean} [disabled=false] - Si es true, deshabilita el input de archivo.
 * @property {number} [maxSize] - Tamaño máximo del archivo en bytes.
 * @property {function({loading: boolean, fileName: string|boolean, disabled: boolean}):React.ReactNode} children - Función render prop para el contenido del label.
 */

/**
 * @typedef {object} FileUploaderAgnosticChangeEvent
 * @property {object} target - Objeto que contiene información del campo.
 * @property {string} target.name - Nombre del campo.
 * @property {object} target.value - Objeto con la información del archivo.
 * @property {string} target.value.data - Archivo codificado en base64.
 * @property {string} target.value.name - Nombre del archivo.
 * @property {string} target.value.type - Tipo/extensión del archivo.
 */

/**
 * Componente genérico para seleccionar y codificar archivos (sin envío al backend).
 *
 * Maneja la selección de archivos, validación de formato y tamaño, y codificación a base64,
 * devolviendo el archivo mediante un evento compatible con inputs estándar. Ideal para
 * formularios donde el archivo se envía junto con otros datos. Utiliza render props para UI.
 *
 * @component
 * @example
 * <FileUploaderAgnostic
 *   name="documento"
 *   accept={['application/pdf']}
 *   maxSize={5242880}
 *   onSuccess={(e) => setFormData({...formData, [e.target.name]: e.target.value})}
 *   onError={(err) => console.error('Error:', err)}
 * >
 *   {({ loading, fileName, disabled }) => (
 *     <ThemeButton loading={loading} disabled={disabled}>
 *       {fileName || 'Seleccionar Archivo'}
 *     </ThemeButton>
 *   )}
 * </FileUploaderAgnostic>
 *
 * @param {FileUploaderAgnosticProps} props - Las props del componente FileUploaderAgnostic.
 */
export const FileUploaderAgnostic = ({
  idName,
  name,
  accept,
  onSuccess,
  onError,
  disabled = false,
  required = false,
  maxSize = DEFAULT_FILE_SIZE,
  children,
}) => {
  const [loading, setLoading] = useState(false)
  const [fileName, setFileName] = useState(false)

  const handleChangeUpload = async (e) => {
    setLoading(true)
    const file = e.target.files[0]
    if (file) {
      if (accept && accept.length > 0 && !isACorrectFormat(file.name, accept)) {
        setLoading(false)
        return onError({ mssg: 'Archivo con formato no válido' })
      } else if (file.size > maxSize) {
        setLoading(false)
        return onError({ mssg: 'Excede el tamaño máximo' })
      } else {
        setFileName(file.name)
        const fileBase64 = await encodeToBase64(file)
        setLoading(false)
        return onSuccess({
          target: {
            name,
            value: { data: fileBase64, name: file.name, type: file.type.split('/')[1] },
          },
        })
      }
    }
    setLoading(false)
  }
  const nameId = `file-upload-${idName || name}`
  return (
    <div style={{ position: 'relative' }}>
      <input
        id={nameId}
        name={name}
        type="file"
        style={{
          opacity: 0,
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: -1,
        }}
        accept={accept.join(',')}
        onChange={handleChangeUpload}
        disabled={disabled}
        required={required}
      />
      <label htmlFor={nameId} style={{ position: 'relative', zIndex: 1 }}>
        {children({ loading, fileName, disabled })}
      </label>
    </div>
  )
}
